import React from 'react';

import CodeSample from '../components/CodeSample';

import {
  H1,
  H2,
  H3,
  Preamble,
  Paragraph,
  Code,
  Link,
  StatusCard,
} from '@aurora/widgets-react';

import { OpenOutline } from '@aurora/icons-react';

const Authentication = () => (
  <React.Fragment>
    <H1>Authentication</H1>
    <Preamble>
      To be able to make valid requests to the <Code>history</Code> or{' '}
      <Code>forecast</Code> services, a customer must first authenticate with
      the <Code>auth</Code> service and retrieve an access token. A customer is
      identified with a unique customernr and a secret.
    </Preamble>
    <H2>Retrieve Access Token</H2>
    <Paragraph>
      The access token is retrieved by making a <Code>POST</Code> request to{' '}
      <Code>https://enlossws.smhi.se/api/auth/login</Code> with the customernr
      and secret provided using{' '}
      <Link to="https://datatracker.ietf.org/doc/html/rfc7617#section-2">
        HTTP Basic Authentication <OpenOutline />
      </Link>
      . If you are using a modern HTTP client, chances are high it provides
      functionality to automatically create the Authorization header for you.
    </Paragraph>
    <H3>Example</H3>
    <Paragraph>
      Given a customer with customernr <Code>12345</Code> and a secret{' '}
      <Code>+SByrZWplbc3uSQms4qjhF7x</Code>, an access token can be retrieved
      with
      <CodeSample
        componentString={`curl -X POST \\\n  -u 12345:+SByrZWplbc3uSQms4qjhF7x \\\n  https://enlossws.smhi.se/api/auth/login`}
        language="bash"
      ></CodeSample>
    </Paragraph>
    <Paragraph>
      If the provided credentials was valid, the <Code>auth</Code> service
      responds with <Code>201 Created</Code> and
      <CodeSample
        componentString={`{"accessToken": "somebase64string"}`}
        language="json"
      />
    </Paragraph>
    <Paragraph>
      The returned access token is a JWT and you are free to decode it since it
      does not include any sensitive data. You should however treat the access
      token as opaque since we may make changes to its structure without notice.
    </Paragraph>
    <H2>Using the Access Token</H2>
    <Paragraph>
      When making requests to the <Code>history</Code> and <Code>forecast</Code>{' '}
      services, the retrieved access token must be provided using{' '}
      <Link to="https://datatracker.ietf.org/doc/html/rfc6750#section-2.1">
        HTTP Bearer Authentication <OpenOutline />
      </Link>
    </Paragraph>
    <H3>Example</H3>
    <Paragraph>
      Given a customer has retrieved an access token{' '}
      <Code>somebase64string</Code>, a request to the <Code>history</Code>{' '}
      service can be made with
      <CodeSample
        componentString={`curl -H "Authorization:Bearer somebase64string" \\\n  'https://enlossws.smhi.se/api/history?placenr=102612&parameternrs=5'`}
        language="bash"
      ></CodeSample>
    </Paragraph>
    <H2>Access Token Properties</H2>
    <Paragraph>
      The access tokens expires after 24 hours. It is therefore recommended to
      always retrieve a new access token for every session.
    </Paragraph>
    <H2>Obtaining customernr and secret</H2>
    <Paragraph>
      The customernr is not sensitive and will be sent to customers with an
      email. The secret however should be treated as any password and will thus
      not be sent with an email. All customers will receive an email with an url
      that will reveal the secret when clicked.
    </Paragraph>
    <StatusCard variant={StatusCard.variants.warning}>
      Make sure to copy the secret when it is revealed! The url is only valid
      for 24 hours.
    </StatusCard>
    <Paragraph>
      The unhashed secret is never persisted on our side meaning there is no way
      for us to retrieve a lost secret. If a secret is lost, contact us to
      generate a new secret for you.
    </Paragraph>

    <StatusCard variant={StatusCard.variants.error}>
      Treat the secret as any password and keep it as protected as possible!
    </StatusCard>

    <H2>Frontend Recommendations</H2>
    <Paragraph>
      If a customer is developing a frontend service using this API as a
      backend, the client should not have a copy of the secret! Only access
      tokens should be provided to the client and they can be retrieved e.g.
      when the user logs in.
    </Paragraph>
  </React.Fragment>
);

export default Authentication;
